<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";

import { ROUTES } from '@/constants/routes';
import { formatFullName } from "@/helpers/name";
import { formatPhone } from "@/helpers/phone";
import { useAuthStore } from "@/modules/auth/store/authStore";
import workerService from "@/modules/worker/services/workerService";
import { type CandidateStatus, ESearchWorkerModes } from '@/types/worker';

import Alert from "@/dsComponents/Alert/Alert.vue";
import { BadgeColor } from "@/dsComponents/Badge";
import Badge from "@/dsComponents/Badge/Badge.vue";
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

const { t } = useI18n();
const router = useRouter();
const toaster = useToast();
const authStore = useAuthStore();
interface UserExists {
    firstname: string;
    lastname: string;
    agencies: {
        id: string;
        name: string;
        city: string;
        active: boolean;
        candidateStatus?: CandidateStatus;
    }[];
    email: string;
    phone: string;
    city: string;
    nir: string;
    id: string;
    candidateId?: string;
}

const emits = defineEmits<(e: "close") => void>();

const props = defineProps<{
    open: boolean;
    user: UserExists;
}>();

const close = () => {
    emits("close");
};

const fullName = computed(() => {
    return formatFullName({ firstname: props.user.firstname, lastname: props.user.lastname });
});
const phoneFormatted = computed(() => formatPhone(props.user.phone));
const agenciesData = computed(() =>
    props.user.agencies.map((agency) => ({
        name: `${agency.name} ${agency.city}`,
        isActive: agency.active,
        id: agency.id,
        ...(agency.candidateStatus ? { candidateStatus: agency.candidateStatus } : {}),
    }))
);
const formettedNir = computed(() => props.user.nir.replace(/(.)(..)(..)(..)(...)(...)/, "$1 $2 $3 $4 $5 $6"));
const isPartofAgency = computed(() => props.user.agencies.some((agency) => agency.id === authStore.currentAgency?.id));

const goToProfile = () => {
    if (props.user.candidateId) {
        router.push({ name: ROUTES.APP.WORKERS.PROFIL.CAREER, params: { workerId: props.user.candidateId } });
        close();
    } else {
        router.push({ name: ROUTES.APP.WORKERS.PROFIL._ROOT, params: { workerId: props.user.id } });
        close();
    }
};

const integrateInterimLoading = ref(false);
const integrateInterim = async () => {
    try {
        integrateInterimLoading.value = true;
        const params = {
            databaseOrigin: ESearchWorkerModes.INTERIM,
            importTo: ESearchWorkerModes.INTERIM,
            workerId: props.user.id,
        };
        const result: { origin: string; id: string } = await workerService.shareWorker(params);

        toaster.open({
            message: t("modals.nirAlreadyExistsModal.toaster.integrateSuccess"),
            type: "success",
            duration: 5000,
        });

        router.push({ name: ROUTES.APP.WORKERS.PROFIL._ROOT, params: { workerId: result.id } });
        close();
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    } catch (error: any) {
        if (error.fullErr.response.data.locale === "error.errorWithEvoliaUpdate") {
            toaster?.open({
                message: t("modals.nirAlreadyExistsModal.toaster.evoliaUpdateError"),
                type: "error",
                duration: 5000,
            });
            return;
        }

        toaster?.open({
            message: t("modals.nirAlreadyExistsModal.toaster.integrateError"),
            type: "error",
            duration: 5000,
        });
    } finally {
        integrateInterimLoading.value = false;
    }
};
</script>

<template>
    <Modal :open="open" @close="close" :title="t('modals.nirAlreadyExistsModal.title')" width="500px">
        <div class="modal-container">
            <Alert icon="alert-triangle-filled" :message="t('modals.nirAlreadyExistsModal.content', { nir: formettedNir })" variant="red" />
            <div class="modal-form">
                <div class="item-title">
                    {{ t("modals.nirAlreadyExistsModal.form.agencies", agenciesData.length) }}
                </div>
                <div class="item-agencies">
                    <div class="item-agency" v-for="agency in agenciesData" :key="agency.id">
                        <TextInputAlt :model-value="agency.name" iconLeft="building-estate" disabled />
                        <Badge
                            v-if="!agency.candidateStatus"
                            :label="agency.isActive ? t('page.worker.status.active') : t('page.worker.status.inactive')"
                            :color="agency.isActive ? BadgeColor.green : BadgeColor.red"
                            stroke
                        />
                        <Badge
                            v-else
                            :label="agency.candidateStatus ? t(`page.candidates.status.${agency.candidateStatus}`) : ''"
                            :color="BadgeColor.neutral"
                            stroke
                        />
                    </div>
                </div>
                <div class="item-title">
                    {{ t("modals.nirAlreadyExistsModal.form.nameLastname") }}
                </div>
                <div>
                    <TextInputAlt :model-value="fullName" iconLeft="doc-desc" disabled />
                </div>
                <div class="item-title">
                    {{ t("modals.nirAlreadyExistsModal.form.phone") }}
                </div>
                <div>
                    <TextInputAlt :model-value="phoneFormatted" iconLeft="phone" disabled />
                </div>
                <div class="item-title">
                    {{ t("modals.nirAlreadyExistsModal.form.email") }}
                </div>
                <div>
                    <TextInputAlt :model-value="user.email" iconLeft="at" disabled />
                </div>
                <div class="item-title">
                    {{ t("modals.nirAlreadyExistsModal.form.city") }}
                </div>
                <div>
                    <TextInputAlt :model-value="user.city" iconLeft="map-pin" disabled />
                </div>
            </div>
        </div>
        <template #footer>
            <div class="button-container">
                <ButtonAlt type="neutral" @click="close">{{ t("global.actions.cancel") }}</ButtonAlt>
                <ButtonAlt v-if="isPartofAgency" variant="filled" @click="goToProfile">
                    {{ t("modals.nirAlreadyExistsModal.action.goToProfile") }}
                </ButtonAlt>
                <ButtonAlt v-else variant="filled" @click="integrateInterim" :loading="integrateInterimLoading">
                    {{ t("modals.nirAlreadyExistsModal.action.integrate") }}
                </ButtonAlt>
            </div>
        </template>
    </Modal>
</template>
<style scoped lang="scss">
.modal-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-xl, 16px);
    align-self: stretch;

    .modal-form {
        display: grid;
        grid-template-columns: 120px 1fr;
        gap: var(--spacing-md, 8px);
        width: 100%;

        & > * {
            min-height: 28px;
            display: flex;
            align-items: center;
        }

        .item-title {
            @include font-size-12;
            font-weight: 500;
            white-space: nowrap;
            gap: var(--spacing-xs, 4px);
            color: var(--text-sub-600, #494e61);
            &.align-top {
                padding-top: var(--spacing-xxs, 2px);
                align-self: flex-start;
            }
        }

        .item-agencies {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-xs, 4px);
        }

        .item-agency {
            display: flex;
            flex-direction: row;
            gap: var(--spacing-xs, 4px);
        }
    }
}

.button-container {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-lg, 12px);
}
</style>
